import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  RefreshCcw,
  Loader2,
  ExternalLink,
  Activity,
  AlertTriangle,
  CheckCircle2,
  Zap,
} from "lucide-react";

const STATUS_STYLES = {
  healthy: {
    border: "border-emerald-300",
    bg: "bg-emerald-50/60",
    pill: "bg-emerald-600",
    icon: <CheckCircle2 className="h-4 w-4 text-emerald-700" />,
    label: "Healthy",
  },
  low: {
    border: "border-amber-300",
    bg: "bg-amber-50/60",
    pill: "bg-amber-500",
    icon: <Zap className="h-4 w-4 text-amber-700" />,
    label: "Low",
  },
  critical: {
    border: "border-rose-300",
    bg: "bg-rose-50/60",
    pill: "bg-rose-600",
    icon: <AlertTriangle className="h-4 w-4 text-rose-700" />,
    label: "Critical",
  },
  unknown: {
    border: "border-stone-300",
    bg: "bg-stone-50/60",
    pill: "bg-stone-500",
    icon: <Activity className="h-4 w-4 text-stone-600" />,
    label: "Unknown",
  },
  error: {
    border: "border-rose-300",
    bg: "bg-rose-50/40",
    pill: "bg-rose-500",
    icon: <AlertTriangle className="h-4 w-4 text-rose-700" />,
    label: "Error",
  },
};

function fmtRunway(p) {
  if (typeof p.runway_videos_remaining === "number") {
    return `~${p.runway_videos_remaining} videos remaining`;
  }
  return null;
}

function fmtAge(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  const sec = Math.round((Date.now() - d.getTime()) / 1000);
  if (sec < 60) return `${sec}s ago`;
  if (sec < 3600) return `${Math.round(sec / 60)}m ago`;
  return `${Math.round(sec / 3600)}h ago`;
}

function ProviderCard({ provider, onRefresh }) {
  const [loading, setLoading] = useState(false);
  const styles = STATUS_STYLES[provider.status] || STATUS_STYLES.unknown;
  const bal = provider.balance;
  const quota = provider.quota;

  const refreshOne = async () => {
    setLoading(true);
    try {
      await api.get(`/credits/${provider.provider}`);
      await onRefresh();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid={`credits-card-${provider.provider}`}
      className={`flex flex-col gap-2 rounded-xl border p-4 shadow-sm ${styles.border} ${styles.bg}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-sm font-bold text-stone-800">
            {styles.icon}
            <span className="truncate">{provider.label}</span>
          </div>
          <div className="text-[11px] uppercase tracking-wide text-stone-500">
            {fmtAge(provider.last_checked_at)}
          </div>
        </div>
        <span
          className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase text-white ${styles.pill}`}
        >
          {styles.label}
        </span>
      </div>

      {bal && bal.display ? (
        <div className="text-xl font-bold text-stone-800">{bal.display}</div>
      ) : (
        <div className="text-sm italic text-stone-500">
          {provider.error || "No data"}
        </div>
      )}

      {quota && quota.percent_used != null ? (
        <div>
          <div className="mb-1 flex justify-between text-[11px] text-stone-600">
            <span>
              {quota.used?.toLocaleString?.() || quota.used} /{" "}
              {quota.limit?.toLocaleString?.() || quota.limit} {quota.unit}
            </span>
            <span className="font-semibold">
              {Math.round(100 - quota.percent_used)}% left
            </span>
          </div>
          <div className="h-1.5 overflow-hidden rounded-full bg-stone-200">
            <div
              className={`h-full ${
                quota.percent_used > 90
                  ? "bg-rose-500"
                  : quota.percent_used > 75
                  ? "bg-amber-500"
                  : "bg-emerald-500"
              }`}
              style={{ width: `${Math.min(100, quota.percent_used)}%` }}
            />
          </div>
        </div>
      ) : null}

      {fmtRunway(provider) ? (
        <div className="text-xs font-medium text-stone-700">
          {fmtRunway(provider)}
        </div>
      ) : null}

      {provider.error && bal ? (
        <div className="text-[11px] text-amber-700">⚠ {provider.error}</div>
      ) : null}

      <div className="mt-1 flex items-center gap-2">
        {provider.top_up_url ? (
          <a
            href={provider.top_up_url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`credits-topup-${provider.provider}`}
            className="inline-flex items-center gap-1 rounded-md border border-stone-300 bg-white px-2 py-1 text-xs font-medium text-stone-700 hover:bg-stone-50"
          >
            Top Up <ExternalLink className="h-3 w-3" />
          </a>
        ) : null}
        <Button
          variant="ghost"
          size="sm"
          disabled={loading}
          onClick={refreshOne}
          data-testid={`credits-refresh-${provider.provider}`}
          className="ml-auto h-7 px-2 text-xs"
        >
          {loading ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : (
            <RefreshCcw className="h-3 w-3" />
          )}
        </Button>
      </div>
    </div>
  );
}

export default function CreditsPanel() {
  const [data, setData] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const load = async (force = false) => {
    setRefreshing(force);
    try {
      const r = force
        ? await api.post("/credits/refresh")
        : await api.get("/credits");
      setData(r.data);
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    load(false);
    const t = setInterval(() => load(false), 60000);
    return () => clearInterval(t);
  }, []);

  if (!data) {
    return (
      <div className="flex items-center gap-2 text-stone-500">
        <Loader2 className="h-4 w-4 animate-spin" /> loading credits…
      </div>
    );
  }

  const providers = data.providers || [];
  const critical = providers.filter((p) => p.status === "critical").length;
  const low = providers.filter((p) => p.status === "low").length;

  return (
    <div className="space-y-3" data-testid="credits-panel">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div
          data-testid="credits-summary"
          className={`text-sm font-medium ${
            critical > 0
              ? "text-rose-700"
              : low > 0
              ? "text-amber-800"
              : "text-emerald-700"
          }`}
        >
          {critical > 0
            ? `🔴 ${critical} critical · ${low} low — top up to keep generating`
            : low > 0
            ? `🟡 ${low} provider${low > 1 ? "s" : ""} running low`
            : "🟢 All providers healthy"}
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => load(true)}
          disabled={refreshing}
          data-testid="credits-refresh-all"
          className="border-orange-300"
        >
          {refreshing ? (
            <Loader2 className="mr-1 h-3 w-3 animate-spin" />
          ) : (
            <RefreshCcw className="mr-1 h-3 w-3" />
          )}
          Refresh
        </Button>
      </div>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {providers.map((p) => (
          <ProviderCard key={p.provider} provider={p} onRefresh={() => load(false)} />
        ))}
      </div>
    </div>
  );
}
